import { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { adminClient, formatApiError } from "@/admin/api";

const QUESTION_TYPES = ["text", "select", "radio", "checkbox", "date", "number"];
const OPTION_TYPES = ["select", "radio", "checkbox"];

export const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 50);

const EMPTY = {
  id: "",
  name: "",
  category: "",
  tag: "",
  desc: "",
  consent_text: "",
  consent_version: "v1.0",
  consent_required: true,
  questions: [],
};

const inputCls =
  "mt-1.5 w-full bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm px-3.5 py-2.5 font-sans text-sm text-[#1A1715] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C]";
const labelCls =
  "block font-mono text-[10px] uppercase tracking-[0.18em] text-[#44403C] font-semibold";

const CampaignEditor = ({ initial, onCancel, onSaved }) => {
  const editing = !!initial;
  const [c, setC] = useState(
    initial
      ? { ...EMPTY, ...initial, questions: initial.questions || [] }
      : EMPTY
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setC((p) => ({ ...p, [k]: v }));
  };

  const setQuestion = (i, patch) =>
    setC((p) => {
      const qs = [...p.questions];
      qs[i] = { ...qs[i], ...patch };
      return { ...p, questions: qs };
    });
  const addQuestion = () =>
    setC((p) => ({
      ...p,
      questions: [
        ...p.questions,
        { id: "", label: "", type: "text", required: false, options: [] },
      ],
    }));
  const removeQuestion = (i) =>
    setC((p) => ({ ...p, questions: p.questions.filter((_, x) => x !== i) }));

  const save = async () => {
    setError("");
    if (!c.name.trim()) return setError("Campaign name is required.");
    if (!editing && !/^[a-z0-9_]{2,60}$/.test(c.id))
      return setError(
        "Campaign ID must be 2-60 chars of lowercase letters, numbers, underscores (e.g. mesothelioma_v2)."
      );
    if (c.consent_required && !c.consent_text.trim())
      return setError("Consent text is required when consent is required.");
    if (!c.consent_version.trim())
      return setError("Consent version is required (e.g. v1.0).");
    const seen = new Set();
    for (const q of c.questions) {
      if (!q.label.trim() || !/^[a-z0-9_]{2,60}$/.test(q.id))
        return setError(
          "Every question needs a label and a valid ID (lowercase letters, numbers, underscores)."
        );
      if (seen.has(q.id)) return setError(`Duplicate question ID: ${q.id}`);
      seen.add(q.id);
      if (OPTION_TYPES.includes(q.type) && q.options.filter(Boolean).length === 0)
        return setError(`Question "${q.label}" needs at least one option.`);
    }

    const payload = {
      ...c,
      questions: c.questions.map((q) => ({
        ...q,
        options: OPTION_TYPES.includes(q.type)
          ? q.options.map((o) => o.trim()).filter(Boolean)
          : [],
      })),
    };
    setSaving(true);
    try {
      if (editing) await adminClient().put(`/admin/campaigns/${c.id}`, payload);
      else await adminClient().post("/admin/campaigns", payload);
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="campaign-editor"
      className="bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm p-6 sm:p-8"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-serif font-semibold text-2xl text-[#1A1715]">
          {editing ? `Edit — ${initial.name}` : "New Campaign"}
        </h2>
        <button
          type="button"
          data-testid="editor-cancel-button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-[#78716C] hover:text-[#C2410C] transition-colors"
        >
          <X className="h-4 w-4" /> Cancel
        </button>
      </div>

      {error && (
        <p
          data-testid="editor-error"
          role="alert"
          className="mt-6 border border-[#FECACA] bg-[#FEF2F2] rounded-sm px-4 py-3 font-sans text-sm font-medium text-[#B91C1C]"
        >
          {error}
        </p>
      )}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="ce-name" className={labelCls}>
            Campaign Name *
          </label>
          <input
            id="ce-name"
            data-testid="editor-name-input"
            className={inputCls}
            value={c.name}
            onChange={set("name")}
            placeholder="e.g. Mesothelioma & Asbestos"
          />
        </div>
        <div>
          <label htmlFor="ce-id" className={labelCls}>
            Campaign ID * {editing && "(locked)"}
          </label>
          <input
            id="ce-id"
            data-testid="editor-id-input"
            className={`${inputCls} font-mono disabled:bg-[#F4EFEA] disabled:text-[#78716C]`}
            value={c.id}
            disabled={editing}
            onChange={set("id")}
            onBlur={() => {
              if (!editing && !c.id && c.name) setC((p) => ({ ...p, id: slugify(p.name) }));
            }}
            placeholder="e.g. mesothelioma"
          />
        </div>
        <div>
          <label htmlFor="ce-category" className={labelCls}>
            Category
          </label>
          <input
            id="ce-category"
            data-testid="editor-category-input"
            className={inputCls}
            value={c.category}
            onChange={set("category")}
            placeholder="e.g. Occupational Hazard"
          />
        </div>
        <div>
          <label htmlFor="ce-tag" className={labelCls}>
            Tag / Badge
          </label>
          <input
            id="ce-tag"
            data-testid="editor-tag-input"
            className={inputCls}
            value={c.tag}
            onChange={set("tag")}
            placeholder="e.g. Trust Funds Active"
          />
        </div>
      </div>
      <div className="mt-6">
        <label htmlFor="ce-desc" className={labelCls}>
          Description
        </label>
        <textarea
          id="ce-desc"
          data-testid="editor-desc-input"
          rows={3}
          className={`${inputCls} resize-y`}
          value={c.desc}
          onChange={set("desc")}
          placeholder="Short public description shown on the case card."
        />
      </div>

      <div className="mt-10 border-t border-[#E7E2DA] pt-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-serif font-semibold text-xl text-[#1A1715]">
              Qualification Questions
            </h3>
            <p className="mt-1 font-sans text-xs text-[#78716C]">
              Rendered dynamically on the public form when this campaign is
              selected. Validated server-side.
            </p>
          </div>
          <button
            type="button"
            data-testid="add-question-button"
            onClick={addQuestion}
            className="inline-flex items-center gap-1.5 border border-[#E7E2DA] rounded-sm px-3.5 py-2.5 font-sans text-xs font-semibold text-[#44403C] hover:border-[#C2410C] hover:text-[#C2410C] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Add Question
          </button>
        </div>

        <div className="mt-5 space-y-4">
          {c.questions.length === 0 && (
            <p
              data-testid="no-questions-note"
              className="font-news italic text-sm text-[#78716C]"
            >
              No questions yet — the public form will show only the standard
              contact fields for this campaign.
            </p>
          )}
          {c.questions.map((q, i) => (
            <div
              key={i}
              data-testid={`question-editor-${i}`}
              className="border border-[#ECE7DF] bg-[#FBF9F5] rounded-sm p-4 sm:p-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                <div className="sm:col-span-5">
                  <label className={labelCls} htmlFor={`q-label-${i}`}>
                    Label *
                  </label>
                  <input
                    id={`q-label-${i}`}
                    data-testid={`question-label-${i}`}
                    className={inputCls}
                    value={q.label}
                    onChange={(e) => {
                      const patch = { label: e.target.value };
                      if (!q.id || q.id === slugify(q.label)) patch.id = slugify(e.target.value);
                      setQuestion(i, patch);
                    }}
                    placeholder="e.g. When were you diagnosed?"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className={labelCls} htmlFor={`q-id-${i}`}>
                    Question ID *
                  </label>
                  <input
                    id={`q-id-${i}`}
                    data-testid={`question-id-${i}`}
                    className={`${inputCls} font-mono`}
                    value={q.id}
                    onChange={(e) => setQuestion(i, { id: slugify(e.target.value) })}
                    placeholder="diagnosis_date"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor={`q-type-${i}`}>
                    Type
                  </label>
                  <select
                    id={`q-type-${i}`}
                    data-testid={`question-type-${i}`}
                    className={inputCls}
                    value={q.type}
                    onChange={(e) => setQuestion(i, { type: e.target.value })}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2 flex items-end justify-between gap-2 pb-1">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`question-required-${i}`}
                      checked={q.required}
                      onChange={(e) => setQuestion(i, { required: e.target.checked })}
                      className="h-4 w-4 accent-[#C2410C]"
                    />
                    <span className="font-sans text-xs font-semibold text-[#44403C]">
                      Required
                    </span>
                  </label>
                  <button
                    type="button"
                    data-testid={`question-remove-${i}`}
                    onClick={() => removeQuestion(i)}
                    className="text-[#A8A29E] hover:text-[#B91C1C] transition-colors"
                    aria-label="Remove question"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {OPTION_TYPES.includes(q.type) && (
                <div className="mt-4">
                  <label className={labelCls} htmlFor={`q-options-${i}`}>
                    Options (comma-separated) *
                  </label>
                  <input
                    id={`q-options-${i}`}
                    data-testid={`question-options-${i}`}
                    className={inputCls}
                    value={q.options.join(", ")}
                    onChange={(e) =>
                      setQuestion(i, {
                        options: e.target.value.split(",").map((s) => s.trim()),
                      })
                    }
                    placeholder="Option A, Option B, Option C"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 border-t border-[#E7E2DA] pt-8">
        <h3 className="font-serif font-semibold text-xl text-[#1A1715]">
          Consent & Disclosure
        </h3>
        <p className="mt-1 font-sans text-xs text-[#78716C]">
          Exactly what the visitor sees is stored with every lead. Editing this
          later does not change historical lead records — bump the version when
          you change the text.
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="sm:col-span-2">
            <label htmlFor="ce-consent-text" className={labelCls}>
              Consent Text *
            </label>
            <textarea
              id="ce-consent-text"
              data-testid="editor-consent-text"
              rows={5}
              className={`${inputCls} resize-y`}
              value={c.consent_text}
              onChange={set("consent_text")}
              placeholder="Full disclosure shown above the submit button…"
            />
          </div>
          <div className="space-y-5">
            <div>
              <label htmlFor="ce-consent-version" className={labelCls}>
                Consent Version *
              </label>
              <input
                id="ce-consent-version"
                data-testid="editor-consent-version"
                className={`${inputCls} font-mono`}
                value={c.consent_version}
                onChange={set("consent_version")}
                placeholder="v1.0"
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="editor-consent-required"
                checked={c.consent_required}
                onChange={set("consent_required")}
                className="mt-0.5 h-4 w-4 accent-[#C2410C]"
              />
              <span className="font-sans text-xs font-semibold text-[#44403C]">
                Consent is required to submit this campaign's form
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="mt-10 flex items-center justify-end gap-4 border-t border-[#E7E2DA] pt-6">
        <button
          type="button"
          onClick={onCancel}
          className="font-sans text-sm font-semibold text-[#78716C] hover:text-[#1A1715] transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="save-campaign-button"
          onClick={save}
          disabled={saving}
          className="bg-[#C2410C] text-[#FBF9F5] font-sans font-semibold text-sm px-7 py-3 rounded-sm transition-colors duration-300 hover:bg-[#9A3412] disabled:opacity-60"
        >
          {saving ? "Saving…" : editing ? "Save Changes" : "Create Campaign"}
        </button>
      </div>
    </div>
  );
};

export default CampaignEditor;
